/**
 * useElementEvents Hook
 * 
 * A React hook that creates and manages element events with minimal boilerplate.
 * This hook automatically handles event schema registration and provides a simplified
 * event broadcasting interface.
 */

import { useMemo, useEffect } from 'react';
import { createElementEvents } from '../utils/element-events';
import { registerElementEventSchemas } from '../utils/register-schemas';
import type { SchemaDefinition } from '../utils/event-schemas';

/**
 * Hook for managing element events with minimal boilerplate
 * 
 * @param elementType - The type of element (e.g., 'proctoring/session-viewer')
 * @param elementId - The unique ID of the element instance
 * @param eventSchemas - The event schemas for the element
 * @param getEventName - Function to convert base event name to full event name
 * @returns An object with the broadcast function for sending events
 */
export function useElementEvents(
  elementType: string,
  elementId: string,
  eventSchemas: Record<string, SchemaDefinition>,
  getEventName?: (eventName: string) => string
) {
  // Create the element events context
  const elementEvents = useMemo(() => {
    return createElementEvents(elementType, elementId, getEventName);
  }, [elementType, elementId, getEventName]);
  
  // Register event schemas when the component mounts
  useEffect(() => {
    registerElementEventSchemas(elementType, eventSchemas);
  }, [elementType, eventSchemas]);
  
  return elementEvents;
}
